/**
 * @testrelic/playwright-analytics/fixture
 *
 * Extended Playwright test fixture that wraps the default `page` fixture
 * to automatically track navigation events and API requests.
 *
 * Exports:
 * - testRelicFixture: Fixture object for use with base.extend()
 * - test: Pre-extended test instance (backward compatibility)
 * - expect: Re-exported from @playwright/test (backward compatibility)
 */

import { test as base, expect as playwrightExpect } from '@playwright/test';
import { NavigationTracker } from './navigation-tracker.js';
import { ApiRequestTracker } from './api-request-tracker.js';
import { AssertionTracker, createActiveAssertionAwareExpect, setActiveAssertionContext } from './assertion-tracker.js';
import { PAYLOAD_VERSION, ATTACHMENT_NAME, ATTACHMENT_CONTENT_TYPE } from '@testrelic/core';
import type { TestRelicFilePayload } from '@testrelic/core';
import type { ResolvedApiConfig } from './config.js';
// Side-effect import: registers `toMatchVisualBaseline` on Playwright's expect,
// so the `expect` exported below carries it without a second import in tests.
import './visual.js';
import { withVisualCapture } from './visual-expect.js';

/**
 * Assertion-aware `expect`. Inside a test that ran through this fixture, each
 * assertion on an API response is recorded and linked to its originating call,
 * so it surfaces in the cloud report's API workspace; outside one it behaves
 * exactly like Playwright's `expect`. Import THIS `expect` (not the one from
 * '@playwright/test') for API assertion capture.
 */
/**
 * `test.info()` throws outside a running test. The visual layer must degrade
 * to a plain pass-through there rather than take an assertion down with it.
 */
function currentTestInfo(): ReturnType<typeof base.info> | null {
  try {
    return base.info();
  } catch {
    return null;
  }
}

/**
 * Two single-purpose layers, composed.
 *
 * The inner layer records API assertions. The outer one attaches DOM
 * comparison to `toHaveScreenshot` and `toMatchVisualBaseline`, which is why
 * visual assertions written with THIS `expect` report which elements changed
 * while ones written with Playwright's own `expect` compare pixels only.
 */
export const expect = withVisualCapture(
  createActiveAssertionAwareExpect(
    playwrightExpect as unknown as (...args: unknown[]) => unknown,
  ),
  currentTestInfo,
) as unknown as typeof playwrightExpect;

/** Annotation type for API config (reporter → fixture). */
const API_CONFIG_ANNOTATION = '__testrelic_api_config';

/** Legacy annotation type for backward compatibility. */
const LEGACY_TRACK_API_CALLS_ANNOTATION = '__testrelic_config_trackApiCalls';

/**
 * Reads API config from testInfo annotations pushed by the reporter.
 * Returns a default config if no annotation is found (FR-013).
 */
export function readApiConfig(testInfo: { annotations: Array<{ type: string; description?: string }> }): ResolvedApiConfig {
  // Try new unified annotation first
  const configAnnotation = testInfo.annotations.find(
    (a) => a.type === API_CONFIG_ANNOTATION && a.description !== undefined,
  );
  if (configAnnotation) {
    try {
      const parsed = JSON.parse(configAnnotation.description!, deserializeRegExp);
      return parsed as ResolvedApiConfig;
    } catch {
      // Fall through to defaults
    }
  }

  // Backward compatibility: check legacy trackApiCalls annotation
  const legacyAnnotation = testInfo.annotations.find(
    (a) => a.type === LEGACY_TRACK_API_CALLS_ANNOTATION && a.description !== undefined,
  );
  if (legacyAnnotation) {
    return {
      trackApiCalls: legacyAnnotation.description !== 'false',
      captureRequestHeaders: true,
      captureResponseHeaders: true,
      captureRequestBody: true,
      captureResponseBody: true,
      captureAssertions: true,
      redactHeaders: ['authorization', 'cookie', 'set-cookie', 'x-api-key'],
      redactBodyFields: ['password', 'secret', 'token', 'apiKey', 'api_key'],
      apiIncludeUrls: [],
      apiExcludeUrls: [],
    };
  }

  // No annotation found — return sensible defaults (FR-013)
  return {
    trackApiCalls: true,
    captureRequestHeaders: true,
    captureResponseHeaders: true,
    captureRequestBody: true,
    captureResponseBody: true,
    captureAssertions: true,
    redactHeaders: ['authorization', 'cookie', 'set-cookie', 'x-api-key'],
    redactBodyFields: ['password', 'secret', 'token', 'apiKey', 'api_key'],
    apiIncludeUrls: [],
    apiExcludeUrls: [],
  };
}

/** JSON reviver that reconstructs serialized RegExp objects. */
function deserializeRegExp(_key: string, value: unknown): unknown {
  if (
    typeof value === 'object' &&
    value !== null &&
    (value as Record<string, unknown>).__regexp === true &&
    typeof (value as Record<string, unknown>).source === 'string'
  ) {
    const { source, flags } = value as { source: string; flags: string };
    return new RegExp(source, flags);
  }
  return value;
}

/**
 * Playwright-compatible fixture object for extending `test.extend()`.
 * Provides `page` (with navigation tracking) and `request` (with API tracking).
 *
 * @example
 * import { test as base } from '\@playwright/test';
 * import { testRelicFixture } from '\@testrelic/playwright-analytics';
 * export const test = base.extend(testRelicFixture);
 */
export const testRelicFixture = {
  page: async (
    { page }: { page: import('@playwright/test').Page },
    use: (page: import('@playwright/test').Page) => Promise<void>,
    testInfo: import('@playwright/test').TestInfo,
  ): Promise<void> => {
    const tracker = new NavigationTracker(page as never);
    try {
      await tracker.init();
    } catch {
      // Graceful degradation: continue without SPA detection
    }

    await use(page);

    try {
      const { navigations, networkRequestsFile, networkRequestsCount, consoleLogsFile, consoleLogsCount } = await tracker.getFileData();
      const payload: TestRelicFilePayload = {
        testRelicData: true,
        version: PAYLOAD_VERSION as '2.0.0',
        navigations,
        apiAssertions: [],
        networkRequestsFile,
        networkRequestsCount,
        consoleLogsFile,
        consoleLogsCount,
        apiCallsFile: null,
        apiCallsCount: 0,
      };
      await testInfo.attach(ATTACHMENT_NAME, {
        body: Buffer.from(JSON.stringify(payload)),
        contentType: ATTACHMENT_CONTENT_TYPE,
      });
    } catch {
      // FR-009: fixture without reporter is harmless
    }
    tracker.dispose();
  },

  request: async (
    { request }: { request: import('@playwright/test').APIRequestContext },
    use: (request: import('@playwright/test').APIRequestContext) => Promise<void>,
    testInfo: import('@playwright/test').TestInfo,
  ): Promise<void> => {
    const apiConfig = readApiConfig(testInfo);

    if (!apiConfig.trackApiCalls) {
      await use(request);
      return;
    }

    const assertionTracker = new AssertionTracker();
    const apiTracker = new ApiRequestTracker(request, assertionTracker, apiConfig);
    apiTracker.intercept();
    // Route the exported assertion-aware `expect` to THIS test's tracker.
    setActiveAssertionContext(assertionTracker, apiTracker);

    try {
      await use(request);
    } finally {
      setActiveAssertionContext(null, null);
    }

    try {
      const { apiCallsFile, apiCallsCount, apiStatusMix } = apiTracker.getFileData();
      const apiAssertions = apiConfig.captureAssertions ? assertionTracker.getData() : [];
      const payload: TestRelicFilePayload = {
        testRelicData: true,
        version: PAYLOAD_VERSION as '2.0.0',
        navigations: [],
        apiAssertions,
        networkRequestsFile: null,
        networkRequestsCount: 0,
        consoleLogsFile: null,
        consoleLogsCount: 0,
        apiCallsFile,
        apiCallsCount,
        apiStatusMix,
      };
      await testInfo.attach(ATTACHMENT_NAME, {
        body: Buffer.from(JSON.stringify(payload)),
        contentType: ATTACHMENT_CONTENT_TYPE,
      });
    } catch {
      // Graceful degradation: fixture without reporter is harmless
    }
    apiTracker.dispose();
    assertionTracker.dispose();
  },
};

/**
 * Track a manually-created page for network requests, console logs, and navigations.
 *
 * Use this when your test creates pages via `browser.newContext()` / `context.newPage()`
 * instead of the built-in `page` fixture. Call it right after creating the page,
 * and call the returned cleanup function after `use()`.
 *
 * @example
 * authenticatedPage: async ({ browser }, use, testInfo) => {
 *   const ctx = await browser.newContext({ storageState: AUTH_FILE });
 *   const page = await ctx.newPage();
 *   const cleanup = await trackPage(page, testInfo);
 *   await use(page);
 *   await cleanup();
 *   await ctx.close();
 * },
 */
export async function trackPage(
  page: import('@playwright/test').Page,
  testInfo: import('@playwright/test').TestInfo,
): Promise<() => Promise<void>> {
  const tracker = new NavigationTracker(page as never);
  try {
    await tracker.init();
  } catch {
    // Graceful degradation
  }

  return async () => {
    try {
      const { navigations, networkRequestsFile, networkRequestsCount, consoleLogsFile, consoleLogsCount } = await tracker.getFileData();
      const payload: TestRelicFilePayload = {
        testRelicData: true,
        version: PAYLOAD_VERSION as '2.0.0',
        navigations,
        apiAssertions: [],
        networkRequestsFile,
        networkRequestsCount,
        consoleLogsFile,
        consoleLogsCount,
        apiCallsFile: null,
        apiCallsCount: 0,
      };
      await testInfo.attach(ATTACHMENT_NAME, {
        body: Buffer.from(JSON.stringify(payload)),
        contentType: ATTACHMENT_CONTENT_TYPE,
      });
    } catch {
      // Graceful degradation
    }
    tracker.dispose();
  };
}

/** Pre-extended test instance for backward compatibility. */
export const test = base.extend(testRelicFixture);
